import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellOff,
  CircleCheck,
  ListTodo,
  RotateCw,
  ShoppingBag,
  SlidersHorizontal,
  Timer,
  Wallet,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import orderService from "@/services/orderService";
import pixService from "@/services/pixService";
import { printOrder } from "@/services/printService";
import { showInfo, showWarning } from "@/store/errorStore";
import useSoundStore from "@/store/soundStore";
import { cn } from "@/lib/utils";
import { formatCurrency, formatElapsed } from "@/lib/format";
import { computeMetrics } from "@/components/dashboard/dashboardMetrics";
import OrderListCard from "./OrderListCard";
import OrderDetailsDialog from "./OrderDetailsDialog";
import OrderFiltersSheet from "./OrderFiltersSheet";
import PaymentConfirmDialog from "./PaymentConfirmDialog";
import ReceiptTextDialog from "./ReceiptTextDialog";
import {
  DEFAULT_FILTERS,
  OPEN_STATUSES,
  STATUS_TABS,
  applyFilters,
  buildWhatsAppUrl,
  countActiveFilters,
  getAverageOpenMinutes,
  getTabForStatus,
  sortOrders,
} from "./orderHelpers";

const PAGE_SIZE = 20;
// Atualização em segundo plano (além do aviso de pedido novo via WebSocket)
const AUTO_REFRESH_MS = 30000;

function Metric({ icon: Icon, label, shortLabel, value, unit, className }) {
  return (
    <div className={cn("flex min-w-0 flex-1 items-center gap-2", className)}>
      <Icon className="hidden h-[18px] w-[18px] flex-shrink-0 text-primary min-[400px]:block" />
      <div className="flex min-w-0 flex-col">
        <span
          className="truncate text-[10px] font-semibold uppercase leading-tight tracking-wider text-muted-foreground"
          title={label}
        >
          <span className="sm:hidden">{shortLabel || label}</span>
          <span className="hidden sm:inline">{label}</span>
        </span>
        <span className="truncate font-display text-[15px] font-bold leading-tight text-primary md:text-lg">
          {value}
          {unit && (
            <span className="ml-1 text-[11px] font-semibold text-muted-foreground">{unit}</span>
          )}
        </span>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-xl border bg-card p-4">
          <div className="flex justify-between">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-5 w-16" />
          </div>
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-8 w-full rounded-lg" />
          <Skeleton className="h-1.5 w-full" />
          <Skeleton className="h-9 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, message, actionLabel, onAction, icon: Icon = ShoppingBag }) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
      <Icon className="mb-3 h-12 w-12 text-muted-foreground/40" />
      <p className="font-display text-base font-semibold text-foreground">{title}</p>
      {message && <p className="mt-1 text-sm text-muted-foreground">{message}</p>}
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 flex h-9 items-center gap-1 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary transition-colors hover:bg-muted"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Gestão de Pedidos (docs/tela/code.html): indicadores do dia, abas de
 * status com contagem, filtros avançados e cards com a ação do próximo passo.
 * Carrega todos os pedidos da empresa e filtra no cliente, para que as
 * contagens das abas fiquem sempre corretas.
 */
const OrdersManagement = ({
  initialStatus = "",
  pendingCount = 0,
  focusOrderCode = null,
  onFocusHandled,
}) => {
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncFailed, setSyncFailed] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const [activeTab, setActiveTab] = useState(() => getTabForStatus(initialStatus));
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Troca de status em andamento, por código do pedido
  const [updatingCodes, setUpdatingCodes] = useState({});
  const [orderToCancel, setOrderToCancel] = useState(null);

  // Modais (o pedido fica guardado para a animação de saída)
  const [detailsCode, setDetailsCode] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [receiptOrder, setReceiptOrder] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const soundMuted = useSoundStore((state) => state.muted);
  const toggleSound = useSoundStore((state) => state.toggleMuted);

  // Só a resposta da busca mais recente vale (evita sobrescrever com dado velho)
  const requestSeq = useRef(0);

  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    const seq = ++requestSeq.current;
    if (!silent) setFetching(true);
    try {
      const data = await orderService.getAdminOrders(undefined, silent ? { silent: true } : {});
      if (seq !== requestSeq.current) return;
      setOrders(Array.isArray(data) ? data : []);
      setLastSync(new Date());
      setSyncFailed(false);
      setLoadError(false);
    } catch (error) {
      // Modal global já exibido pelo interceptor (exceto no modo silencioso)
      if (seq !== requestSeq.current) return;
      setSyncFailed(true);
      if (!silent) setLoadError(true);
    } finally {
      // Busca descartada (ex.: remontagem do StrictMode) não encerra o loading:
      // a lista ainda estaria vazia
      if (!silent && seq === requestSeq.current) setFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Atualização automática e relógio dos "há X min"
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
      fetchOrders({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  // Chegou/saiu pedido "Aguardando" (WebSocket/polling do AdminPanel)
  const lastPending = useRef(pendingCount);
  useEffect(() => {
    if (lastPending.current !== pendingCount) {
      lastPending.current = pendingCount;
      fetchOrders({ silent: true });
    }
  }, [pendingCount, fetchOrders]);

  // Atalho do sino do Header com a tela já aberta
  useEffect(() => {
    if (initialStatus) setActiveTab(getTabForStatus(initialStatus));
  }, [initialStatus]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab, filters]);

  const filtered = useMemo(() => applyFilters(orders, filters, now), [orders, filters, now]);

  const tabCounts = useMemo(
    () => Object.fromEntries(STATUS_TABS.map((tab) => [tab.id, filtered.filter(tab.match).length])),
    [filtered],
  );

  const tabOrders = useMemo(() => {
    const tab = STATUS_TABS.find((t) => t.id === activeTab) || STATUS_TABS[0];
    return sortOrders(filtered.filter(tab.match), filters.sortBy);
  }, [filtered, activeTab, filters.sortBy]);

  const visibleOrders = tabOrders.slice(0, visibleCount);

  // Indicadores do dia (sobre todos os pedidos, sem os filtros da lista)
  const today = useMemo(() => computeMetrics(orders, "today", now), [orders, now]);
  const openCount = useMemo(
    () => orders.filter((order) => OPEN_STATUSES.includes(order.status)).length,
    [orders],
  );
  const averageOpen = useMemo(() => getAverageOpenMinutes(orders, now), [orders, now]);

  const activeFilters = countActiveFilters(filters);
  const detailsOrder = useMemo(
    () => orders.find((order) => order.code === detailsCode) || null,
    [orders, detailsCode],
  );

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setActiveTab("all");
  };

  const patchOrder = (code, patch) =>
    setOrders((prev) => prev.map((order) => (order.code === code ? { ...order, ...patch } : order)));

  const handleChangeStatus = async (order, status) => {
    if (updatingCodes[order.code]) return;
    setUpdatingCodes((prev) => ({ ...prev, [order.code]: true }));
    try {
      await orderService.updateStatus(order.code, status);
      patchOrder(order.code, { status });
      fetchOrders({ silent: true });
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setUpdatingCodes(({ [order.code]: _, ...rest }) => rest);
    }
  };

  const handleConfirmCancel = () => {
    const order = orderToCancel;
    setOrderToCancel(null);
    if (order) handleChangeStatus(order, "Cancelado");
  };

  const openDetails = (order) => {
    setDetailsCode(order.code);
    setDetailsOpen(true);
  };

  // Pedido aberto a partir de outra tela (ex.: reserva no Estoque): espera a
  // primeira carga e abre os detalhes. Fora da lista, busca pelo código.
  useEffect(() => {
    if (!focusOrderCode || fetching || (!lastSync && !loadError)) return;
    const code = String(focusOrderCode);
    onFocusHandled?.();

    const showDetails = (found) => {
      setActiveTab("all");
      setDetailsCode(found.code);
      setDetailsOpen(true);
    };

    const listed = orders.find((o) => String(o.code) === code);
    if (listed) {
      showDetails(listed);
      return;
    }

    orderService
      .getOrder(code)
      .then((found) => {
        if (!found?.code) {
          showWarning(`Pedido #${code} não encontrado.`, "Pedido");
          return;
        }
        setOrders((prev) =>
          prev.some((o) => o.code === found.code) ? prev : [found, ...prev],
        );
        showDetails(found);
      })
      .catch(() => {
        // Modal global já exibido pelo interceptor
      });
    // onFocusHandled limpa focusOrderCode: as próximas execuções saem no início
  }, [focusOrderCode, fetching, lastSync, loadError, orders, onFocusHandled]);

  const openPayment = (order) => {
    setPaymentOrder(order);
    setPaymentOpen(true);
  };

  const openReceiptText = (order) => {
    setReceiptOrder(order);
    setReceiptOpen(true);
  };

  const handleWhatsApp = (order) => {
    const url = buildWhatsAppUrl(order);
    if (!url) {
      showWarning("O cliente deste pedido não tem telefone cadastrado.", "WhatsApp");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Envia o pedido para a impressora da loja (app Android da empresa)
  const handleStorePrinter = async (order) => {
    try {
      await orderService.print(order.code);
      showInfo(`Pedido #${order.code} enviado para a impressora da loja.`, "Impressão");
    } catch (error) {
      // Modal global já exibido pelo interceptor
    }
  };

  const handlePaymentConfirm = async (paymentData) => {
    setSavingPayment(true);
    try {
      await pixService.confirmPayment(paymentData.orderCode, paymentData);
      patchOrder(paymentData.orderCode, { payed: true, pay_method: paymentData.pay_method });
      setPaymentOpen(false);
      showInfo(`Pagamento do pedido #${paymentData.orderCode} confirmado.`, "Pagamento");
      fetchOrders({ silent: true });
    } catch (error) {
      // Erros HTTP já abrem o modal global; aqui só validação local
      if (!error.response && !error.request) {
        showWarning(error.message || "Não foi possível confirmar o pagamento.");
      }
    } finally {
      setSavingPayment(false);
    }
  };

  const cardHandlers = {
    onChangeStatus: handleChangeStatus,
    onCancel: setOrderToCancel,
    onDetails: openDetails,
    onPayment: openPayment,
    onWhatsApp: handleWhatsApp,
    onPrint: printOrder,
    onStorePrinter: handleStorePrinter,
    onCopyText: openReceiptText,
  };

  const headerButton =
    "flex h-9 flex-shrink-0 items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-2.5 text-primary shadow-soft transition-colors hover:bg-surface";

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 md:gap-4">
      {/* Título, status da conexão e ações */}
      <section className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-[22px] font-bold leading-7 tracking-tight text-primary md:text-2xl">
              Gestão de Pedidos
            </h1>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                syncFailed
                  ? "border-terra/25 bg-terra-soft text-terra"
                  : "border-brand-pistachio/40 bg-brand-pistachio/15 text-primary",
              )}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  syncFailed ? "bg-terra" : "animate-pulse bg-primary",
                )}
              />
              {syncFailed ? "Offline" : "Online"}
            </span>
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
            <span
              className="inline-flex items-center gap-1"
              title="Média do tempo desde a criação dos pedidos em andamento"
            >
              <Timer className="h-[15px] w-[15px] text-primary" />
              Tempo médio em aberto:
              <span className="font-semibold text-primary">
                {averageOpen === null ? "—" : `${averageOpen} min`}
              </span>
            </span>
            <span className="text-border" aria-hidden="true">
              •
            </span>
            <button
              type="button"
              onClick={() => fetchOrders({ silent: true })}
              className={cn(
                "inline-flex items-center gap-1 text-[11px] hover:underline",
                syncFailed ? "font-semibold text-terra" : "text-muted-foreground",
              )}
              title="Atualizar agora"
            >
              {syncFailed
                ? "Falha ao atualizar · tentar de novo"
                : lastSync
                ? `Atualizado ${formatElapsed(lastSync, now)}`
                : "Atualizando..."}
              <RotateCw className="h-3 w-3" />
            </button>
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={!soundMuted}
            aria-label={soundMuted ? "Ativar som de novos pedidos" : "Silenciar som de novos pedidos"}
            title={soundMuted ? "Som de novos pedidos desligado" : "Som de novos pedidos ligado"}
            className={cn(headerButton, soundMuted && "text-muted-foreground")}
          >
            {soundMuted ? <BellOff className="h-[18px] w-[18px]" /> : <Bell className="h-[18px] w-[18px]" />}
            <span className="hidden text-[11px] font-bold sm:inline">
              {soundMuted ? "Som desligado" : "Som ativo"}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(true)}
            aria-label="Filtros avançados"
            title="Filtros"
            className={cn(headerButton, "relative w-9 px-0")}
          >
            <SlidersHorizontal className="h-[18px] w-[18px]" />
            {activeFilters > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {activeFilters}
              </span>
            )}
          </button>
        </div>
      </section>

      {/* Indicadores do dia */}
      <section
        aria-label="Indicadores de hoje"
        className="flex items-center rounded-xl border bg-card px-3.5 py-2.5 shadow-soft"
      >
        <Metric
          icon={Wallet}
          label="Faturamento hoje"
          shortLabel="Faturamento"
          value={formatCurrency(today.revenue)} />
        <span className="mx-2.5 h-7 w-px flex-shrink-0 bg-border" aria-hidden="true" />
        <Metric
          icon={CircleCheck}
          label="Entregues hoje"
          shortLabel="Entregues"
          value={today.completed}
          unit={today.completed === 1 ? "pedido" : "pedidos"}
        />
        <span className="mx-2.5 h-7 w-px flex-shrink-0 bg-border" aria-hidden="true" />
        <Metric
          icon={ListTodo}
          label="Pendentes"
          value={openCount}
          unit={openCount === 1 ? "ativo" : "ativos"}
        />
      </section>

      {/* Abas de status (scroll horizontal no celular) */}
      <div
        role="group"
        aria-label="Filtrar por status"
        className="no-scrollbar -mx-3.5 flex items-center gap-1.5 overflow-x-auto px-3.5 py-0.5 md:mx-0 md:flex-wrap md:px-0"
      >
        {STATUS_TABS.map((tab) => {
          const active = tab.id === activeTab;
          const count = tabCounts[tab.id] || 0;
          return (
            <button
              key={tab.id}
              type="button"
              aria-pressed={active}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex flex-shrink-0 items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[13px] transition-colors",
                active
                  ? "border-primary bg-primary font-semibold text-primary-foreground shadow-sm"
                  : "border-border bg-card font-medium text-muted-foreground hover:text-primary",
              )}
            >
              {tab.label}
              <span
                className={cn(
                  "rounded-full px-1.5 text-[10px] font-bold",
                  active
                    ? "bg-brand-pistachio text-primary"
                    : count > 0
                    ? "bg-secondary text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {activeFilters > 0 && (
        <div className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
          <span>
            {activeFilters} {activeFilters === 1 ? "filtro ativo" : "filtros ativos"} ·{" "}
            {filtered.length} de {orders.length} pedidos
          </span>
          <button
            type="button"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            className="inline-flex items-center gap-0.5 font-semibold text-primary hover:underline"
          >
            <X className="h-3.5 w-3.5" />
            Limpar filtros
          </button>
        </div>
      )}

      {/* Lista */}
      {fetching && orders.length === 0 ? (
        <ListSkeleton />
      ) : loadError && orders.length === 0 ? (
        <EmptyState
          title="Não foi possível carregar os pedidos"
          message="Verifique a conexão e tente novamente."
          actionLabel="Tentar novamente"
          onAction={() => fetchOrders()}
        />
      ) : tabOrders.length === 0 ? (
        orders.length === 0 ? (
          <EmptyState
            title="Nenhum pedido ainda"
            message="Os pedidos feitos no cardápio aparecem aqui em tempo real."
          />
        ) : (
          <EmptyState
            title="Nenhum pedido encontrado"
            message={
              activeFilters > 0 || activeTab !== "all"
                ? "Não há pedidos nesta aba com os filtros atuais."
                : undefined
            }
            actionLabel={activeFilters > 0 || activeTab !== "all" ? "Ver todos os pedidos" : undefined}
            onAction={clearFilters}
          />
        )
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {visibleOrders.map((order) => (
              <OrderListCard
                key={order.code}
                order={order}
                now={now}
                busy={!!updatingCodes[order.code]}
                {...cardHandlers}
              />
            ))}
          </div>
          {tabOrders.length > visibleCount && (
            <div className="flex flex-col items-center gap-1.5 pt-1">
              <span className="text-xs text-muted-foreground">
                Mostrando {visibleOrders.length} de {tabOrders.length} pedidos
              </span>
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="flex h-9 items-center rounded-lg border border-border bg-card px-4 text-sm font-semibold text-primary shadow-soft transition-colors hover:bg-surface"
              >
                Carregar mais
              </button>
            </div>
          )}
        </>
      )}

      <OrderFiltersSheet
        open={showFilters}
        onOpenChange={setShowFilters}
        filters={filters}
        onChange={setFilters}
        resultCount={tabOrders.length}
      />

      <OrderDetailsDialog
        open={detailsOpen && !!detailsOrder}
        onOpenChange={setDetailsOpen}
        order={detailsOrder}
        onPrint={printOrder}
        onStorePrinter={handleStorePrinter}
        onCopyText={openReceiptText}
        onPayment={openPayment}
        onWhatsApp={handleWhatsApp}
      />

      <PaymentConfirmDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        order={paymentOrder}
        onConfirm={handlePaymentConfirm}
        saving={savingPayment}
      />

      <ReceiptTextDialog open={receiptOpen} onOpenChange={setReceiptOpen} order={receiptOrder} />

      {/* Confirmação de cancelamento */}
      <AlertDialog open={!!orderToCancel} onOpenChange={(open) => !open && setOrderToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar pedido #{orderToCancel?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              O pedido de {orderToCancel?.client?.name || "cliente não informado"} (
              {formatCurrency(orderService.calculateTotal(orderToCancel))}) será marcado como
              cancelado.
              {orderToCancel?.payed && " Ele já está pago: lembre-se de combinar o estorno com o cliente."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancelar pedido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrdersManagement;
